<template>
  <div class="pa-4">
    <div v-if="loading">
      <v-skeleton-loader type="card ,article, actions"></v-skeleton-loader>
    </div>
    <div row wrap v-else-if="!loading" class="pa-0" v-allowed="'manage_flowcharts_data'">
      <v-card elevation="0" class="pa-2" v-if="flowchart">
        <v-text-field @change="updateFlowchartName()" outlined dense :label="$t('message.name')"
          v-model="flowchart.name"></v-text-field>
        <v-textarea @change="updateFlowchartName()" :rows="2" outlined width="100%" v-model="flowchart.description"
          :label="$t('message.description')"></v-textarea>
        <v-alert v-if="isSmallerDisplay()" dense class="warning text-center white--text">
          {{ $t("message.editor_optimized_for_desktop") }}
        </v-alert>
        <div style="width:100%;" v-else>
          <div class="mb-2 top-functions" style="display:flex; align-items:center;">
            <div style="display: flex; align-items: center;">
              <v-icon size="24">mdi-keyboard-close-outline</v-icon>
              <span class="text-caption ml-2">{{ $t('message.use_keyboard') }}</span>
            </div>
            <div>
              <v-col class="pa-0">
                <v-row class="mb-2 mr-1" style="flex-wrap: inherit;">
                  <v-btn elevation="0" class="newbtn" @click="toggleView">
                    <v-icon v-if="viewMode == 'chart'" size="24">mdi-view-list</v-icon>
                    <v-icon v-else size="24">mdi-sitemap-outline</v-icon>
                    <span v-if="viewMode == 'chart'" class="d-none d-md-flex ml-2">{{ $t('message.list') }}</span>
                    <span v-else class="d-none d-md-flex ml-2">{{ $t('message.chart') }}</span>
                  </v-btn>
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-btn elevation="0" class="newbtn ml-3" @click="previewFlowchart" v-on="on">
                        <v-icon size="24">mdi-monitor-eye</v-icon>
                        <span class="d-none d-md-flex ml-2">{{ $t('message.view') }}</span>
                      </v-btn>
                    </template>
                    <span>{{ $t('message.preview_flowchart') }}</span>
                  </v-tooltip>
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-btn elevation="0" class="newbtn ml-3" :disabled="updatingNodes" @click="updateFlowchartNodes"
                        v-on="on">
                        <v-icon class="rotate" v-if="updatingNodes" color="primary">mdi-refresh</v-icon>
                        <v-icon v-if="!updatingNodes" size="24">mdi-checkbox-multiple-marked-outline</v-icon>
                        <span class="d-none d-md-flex ml-2">{{ $t('message.update') }}</span>
                      </v-btn>
                    </template>
                    <span>{{ $t('message.last_update') }}: {{ $d(new Date(flowchart.updatedAt), "long", $i18n.locale)
                    }}</span>
                  </v-tooltip>
                  <v-menu offset-y>
                    <template v-slot:activator="{ on }">
                      <v-btn elevation="0" v-on="on" fab text small slot="activator" left>
                        <v-icon>mdi-dots-vertical</v-icon>
                      </v-btn>
                    </template>
                    <v-list>
                      <v-list-item @click.stop="getFlowchart()">
                        <v-tooltip top>
                          <template v-slot:activator="{ on }">
                            <v-list-item-title v-on="on">
                              <v-icon size="24">mdi-reload-alert</v-icon>
                              <span class="hidden-sm-and-down ml-2">{{ $t('message.reload') }}</span>
                            </v-list-item-title>
                          </template>
                          <span>{{ $t('message.reload_explained') }}</span>
                        </v-tooltip>
                      </v-list-item>
                      <v-list-item @click.stop="archiveFlowchart()">
                        <v-list-item-title>
                          <v-icon size="24">mdi-delete-variant</v-icon>
                          <span class="hidden-sm-and-down ml-2">{{ $t('message.archive') }}</span>
                        </v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </v-row>
              </v-col>
            </div>
            <!-- panel code here -->

          </div>
          <div v-if="viewMode == 'chart'">
            <div class="flowy-wrapper" ref="scrollContainer">
              <flowy class="flowchart-container" :nodes="nodes" :beforeMove="beforeMove" :beforeAdd="beforeAdd"
                @add="add" @move="move" @remove="remove" @drag-start="onDragStart" @keydown.up="moveUp"
                @keydown.down="moveDown" @keydown.left="moveLeft" @keydown.right="moveRight">
              </flowy>
            </div>
          </div>
          <div v-else>
            <v-expansion-panels multiple class="mt-2" flat v-model="nodePanels" v-if="nodes">
              <v-expansion-panel v-for="i of nodes" :key="'node_' + i._id"
                :class="$store.state.theme == 'light' ? 'border-light' : 'border-dark'">
                <v-expansion-panel-header>
                  <div>
                    {{ i.title }}
                    <v-icon class="ml-1" v-if="i.imageUrl">mdi-image-outline</v-icon>
                    <v-icon class="ml-1" v-if="i.adminVideo">mdi-file-video-outline</v-icon>
                    <v-icon class="ml-1" v-if="i.videoUrl">mdi-youtube</v-icon>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <FlowchartNode :node="i" :videos="videos" :static-mode="true"></FlowchartNode>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </div>
      </v-card>
    </div>
    <v-card v-if="viewMode == 'chart'" elevation="0" class="ma-0 pa-6" fill-height
      style="position: fixed; bottom: 0; width: 100%; max-height: 165px;">
      <v-divider class="my-6"></v-divider>
      <div class="overflow-flowy">
        <div class="pa-2 d-flex" v-if="!loading">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn v-bind="attrs" v-on="on" icon small>
                <v-icon>mdi-information-slab-circle-outline</v-icon>
              </v-btn>
            </template>
            <span>
              {{ $t('message.add_nodes') }} - {{ $t('message.add_nodes_explained') }}
            </span>
          </v-tooltip>
          <flowy-new-block v-for="(block, index) in blocks" :key="index" class="mr-2" @drag-start="onDragStartNewBlock"
            @drag-stop="onDragStopNewBlock">
            <template v-slot:preview="{ }">
              <FlowchartBlock :type="block.type" :title="block.title" :description="block.preview.description" />
            </template>
            <template v-slot:node="{ }">
              <FlowchartNode :videos="videos" :n="block.node"></FlowchartNode>
            </template>
          </flowy-new-block>
        </div>
      </div>
    </v-card>
    <div class="view-flowchart">
      <v-dialog v-model="previewDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
        <v-card outlined class="pa-0">
          <v-app-bar flat>
            <v-spacer></v-spacer>
            <v-btn icon @click="previewDialog = false">
              <v-icon>mdi-close-circle-outline</v-icon>
            </v-btn>
          </v-app-bar>
          <div class="flowy-wrapper">
            <flowy v-if="previewDialog" class="flowchart-container" :nodes="previewNodes">
            </flowy>
          </div>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
/* eslint-disable vue/no-unused-components */
import FlowchartBlock from "@/components/flowcharts/FlowchartBlock";
import FlowchartNode from "@/components/flowcharts/FlowchartNode";
import FlowchartNodeView from "@/components/flowcharts/FlowchartNodeView";
import Api from "@/services/Api";

export default {
  data() {
    return {
      addingNew: false,
      flowchartId: this.$route.params.flowchartId,
      loading: true,
      flowchart: null,
      isManager: false,
      updatingNodes: false,
      loadingPreview: false,
      videos: [],
      nodes: [],
      blocks: [],
      previewNodes: [],
      nodePanels: [],
      viewMode: 'chart',
      previewDialog: null,
      techniqueTypes: ['submission', 'pass', 'mount', 'side-control', 'sweep', 'guard', 'back', 'takedown', 'transition', 'standup', 'generic'],
    };
  },
  created() {
    this.loading = true;
    this.init();
    this.buildAvailableBlocks();
  },
  mounted() {
    this.$nextTick(() => {
      window.dispatchEvent(new Event('resize'));
      setTimeout(this.scrollToCenter, 1000); // delay may need tuning
    });
  },
  components: {
    FlowchartNode,
    FlowchartBlock,
    FlowchartNodeView
  },
  methods: {
    scrollToCenter() {
      const container = this.$refs.scrollContainer;
      if (!container) return;

      const scrollWidth = container.scrollWidth;
      const clientWidth = container.clientWidth;
      const scrollLeft = (scrollWidth - clientWidth) / 2;

      container.scrollTo({ left: scrollLeft, behavior: "smooth" });
    },
    toggleView() {
      if (this.viewMode == 'chart')
        this.viewMode = 'list';
      else
        this.viewMode = 'chart'
    },
    async updateFlowchartName() {
      const response = await Api()
        .patch("flowcharts/admins/managedacademy/" + this.$store.state.managedAcademy + '/flowchart/' + this.flowchartId,
          { description: this.flowchart.description, name: this.flowchart.name })
      this.updatingNodes = false;
      if (response) {
        this.showAlert(
          "success",
          this.$t("message.updated")
        );
      }
    },
    previewFlowchart() {
      this.previewDialog = true;
      this.loadChartNodesPreviewMode(this.flowchart.nodeIds);
    },
    getTechniqueTitle(t) {
      let title = this.$t('message.submission')
      if (t == 'submission')
        title = this.$t('message.submission');
      else if (t == 'pass')
        title = this.$t('message.pass');
      else if (t == 'mount')
        title = this.$t('message.mount');
      else if (t == 'side-control')
        title = this.$t('message.side_control');
      else if (t == 'sweep')
        title = this.$t('message.sweep');
      else if (t == 'guard')
        title = this.$t('message.guard');
      else if (t == 'back')
        title = this.$t('message.back');
      else if (t == 'takedown')
        title = this.$t('message.takedown');
      else if (t == 'transition')
        title = this.$t('message.transition');
      else if (t == 'standup')
        title = this.$t('message.standup');
      else if (t == 'start')
        title = this.$t('message.start');
      else if (t == 'generic')
        title = this.$t('message.generic');
      return title;
    },
    loadChartNodesPreviewMode(nodes) {
      this.previewNodes = [];
      let previewNodes = [];
      if (nodes) {
        for (const n of nodes) {
          previewNodes.push(
            {
              id: n.id,
              parentId: n.parentId,
              nodeComponent: FlowchartNodeView,
              type: n.nodeType,
              title: n.title,
              technique: this.getTechniqueTitle(n.nodeType),
              description: n.description,
              adminVideo: n.adminVideo,
              videoUrl: n.videoUrl,
              imageUrl: n.imageUrl,
              nodeIcon: n.nodeIcon,
              flowchartId: this.flowchartId,
              videos: this.videos,
            }
          );
        }
        this.previewNodes = previewNodes;
        this.loading = false;
      }
    },
    loadChartNodes(nodes) {
      let updatedNodes = [];
      if (nodes) {
        for (const n of nodes) {
          updatedNodes.push(
            {
              id: n.id,
              _id: n._id,
              parentId: n.parentId,
              nodeComponent: FlowchartNode,
              type: n.nodeType,
              title: n.title,
              technique: this.getTechniqueTitle(n.nodeType),
              description: n.description,
              adminVideo: n.adminVideo,
              nodeIcon: n.nodeIcon,
              videoUrl: n.videoUrl,
              imageUrl: n.imageUrl,
              flowchartId: this.flowchartId,
              videos: this.videos,
              data: {
                type: 'start',
                title: this.$t('message.add_title'),
                description: this.$t('message.add_description'),
              }
            }
          );
        }
        this.nodes = updatedNodes;
        this.loading = false;
      }
    },
    async updateFlowchartNodes() {
      if (this.nodes) {
        this.updatingNodes = true;
        const updatedNodes = [];
        for (let n of this.nodes) {
          if (n.data && n.data.node) {
            const node = n.data.node;
            updatedNodes.push({
              _id: n._id,
              flowchartId: this.flowchartId,
              id: n.id,
              parentId: n.parentId,
              nodeType: node.type,
              title: node.title,
              description: node.description,
              adminVideo: node.adminVideo ? node.adminVideo._id : null,
              videoUrl: node.videoUrl ? node.videoUrl : null,
              imageUrl: node.imageUrl,
              nodeIcon: node.nodeIcon
            });
          } else if (n.data) {
            updatedNodes.push({
              _id: n._id,
              flowchartId: this.flowchartId,
              id: n.id,
              parentId: n.parentId,
              nodeType: n.type,
              title: n.title,
              description: n.description,
              adminVideo: n.adminVideo ? n.adminVideo._id : null,
              videoUrl: n.videoUrl ? n.videoUrl : null,
              imageUrl: n.imageUrl,
              nodeIcon: n.nodeIcon
            });
          } else if (n.parentId == -1) {
            //Root
            updatedNodes.push({
              _id: n._id,
              flowchartId: this.flowchartId,
              id: n.id,
              parentId: n.parentId,
              nodeType: n.data.type,
              title: n.data.title,
              description: n.data.description,
            });
          }
        }
        const response = await Api()
          .patch("flowcharts/admins/managedacademy/" + this.$store.state.managedAcademy + '/flowchart/' + this.flowchartId + "/nodes", { nodes: updatedNodes })
        this.updatingNodes = false;
        if (response) {
          this.getFlowchart(this.flowchartId);
          this.showAlert(
            "success",
            this.$t("message.updated")
          );
        }
      }
    },
    goBack() {
      this.$router.go(-1);
    },
    getFlowchart() {
      Api()
        .get("flowcharts/admins/managedacademy/" + this.$store.state.managedAcademy + '/flowchart/' + this.flowchartId)
        .then(response => {
          const data = response.data;
          if (data) {
            this.flowchart = data.flowchart;
            this.loading = false;
            if (this.flowchart.nodeIds && this.flowchart.nodeIds.length > 0) {
              this.loadChartNodes(this.flowchart.nodeIds);
            } else {
              //Initialize empty flowchart nodes
              this.nodes = [
                {
                  id: '1',
                  parentId: -1,
                  nodeComponent: FlowchartNode,
                  type: 'start',
                  title: this.$t('message.add_title'),
                  description: this.$t('message.add_description'),
                  technique: this.getTechniqueTitle('start'),
                  data: {
                    type: 'start',
                    title: this.$t('message.add_title'),
                    description: this.$t('message.add_description'),
                  }
                }
              ];
              this.loadingPreview = false;
              this.loading = false;
            }
          }
        })
        .catch(err => {
          console.log(err)
          if (err)
            this.showAlert(
              "error",
              this.$t('message.oops_not_working')
            );
        });
    },
    updateFlowchart() {
      Api().patch('flowcharts/admins/managedacademy/' + this.$store.state.managedAcademy + '/flowchart/' + this.flowchartId, this.flowchart)
        .then(data => {
          if (data) {
            this.showAlert(
              "success",
              this.$t('message.updated')
            );
          }
        })
        .catch(err => {
          if (err)
            this.showAlert(
              "error",
              this.$t('message.oops_not_working')
            );
        });
    },
    moveUp() {
      console.log('Moving up');
    },
    moveDown() {
      console.log('Moving down');
    },
    moveLeft() {
      console.log('Moving left');
    },
    moveRight() {
      console.log('Moving right');
    },
    async init() {
      Api()
        .post("videos/admins/" + this.$store.state.managedAcademy + '/search', {})
        .then(response => {
          if (response.data.videos.length > 0) {
            let videos = [];
            for (const v of response.data.videos) {
              videos.push({
                _id: v._id,
                image: v.thumbnail,
                title: v.title
              });
            }
            this.videos = videos;
          }
          this.getFlowchart();
        })
        .catch(() => {
          this.showAlert("error", "Oops!", "Looks like you should not be here.");
        });
    },
    //Flowy methods
    onDragStartNewBlock(event) {
      //onDragStartNewBlock(event) {
      // contains all the props and attributes passed to FlowchartNode
      const { props } = event
      this.newDraggingBlock = props;
      //console.log('onDragStartNewBlock', event);
    },
    onDragStopNewBlock() {
      //onDragStopNewBlock(event) {
      this.newDraggingBlock = null;
      //console.log('onDragStopNewBlock', event);
    },
    // REQUIRED
    beforeMove({ to, from }) {
      //beforeMove({ to, from }) {
      // called before moving node (during drag and after drag)
      // indicator will turn red when we return false
      // from is null when we're not dragging from the current node tree
      //console.log('beforeMove', to, from);

      // we cannot drag upper parent nodes in this demo
      if (from && from.parentId === -1) {
        return false;
      }
      // we're adding a new node (not moving an existing one)
      if (from === null) {
        // we've passed this attribute to the FlowchartNode
        if (this.newDraggingBlock['custom-attribute'] === false) {
          return false
        }
      }

      if (to)
        return true;
      return true;
    },
    // REQUIRED
    beforeAdd() {
      //beforeAdd({ to, from }) {
      // called before moving node (during drag and after drag)
      // indicator will turn red when we return false
      // from is null when we're not dragging from the current node tree
      //console.log('beforeAdd', to, from);

      // we've passed this attribute to the FlowchartNode
      if (this.newDraggingBlock['custom-attribute'] === false) {
        return false
      }

      return true;
    },
    randomInteger() {
      return Math.floor(Math.random() * 10000) + 1;
    },
    generateId(nodes) {
      let id = null;
      let excludeNumbers = [];
      // _.find is a lodash function
      if (nodes && nodes.length > 0) {
        /*eslint-disable no-undef*/
        excludeNumbers = nodes.map((n) => {
          return n.id;
        });
      }

      do {
        id = this.randomInteger();
      } while (excludeNumbers.includes(id));


      return id + "";
    },
    addNode(_event) {
      const id = this.generateId();
      this.nodes.push({
        ..._event.node,
        id,
      });
    },
    remove(event) {
      //console.log('remove', event)

      // node we're dragging to
      const { node } = event;
      let nodeIndex = 0;
      for (const n of this.nodes) {
        if (n.id == node.id) {
          break;
        }
        nodeIndex++;
      }
      this.nodes.splice(nodeIndex, 1);
    },
    move(event) {

      // node we're dragging to and node we've just dragged
      const { dragged, to } = event;

      // change panentId to id of node we're dragging to
      dragged.parentId = to.id;
    },
    add(event) {
      // every node needs an ID
      const id = this.generateId();
      // add to array of nodes
      const node = event.node;
      this.nodes.push({
        id: id,
        parentId: node.parentId,
        nodeComponent: FlowchartNode,
        type: node.data.n.type,
        title: node.data.n.title,
        technique: this.getTechniqueTitle(node.data.n.type),
        description: node.data.n.description,
        adminVideo: node.data.n.adminVideo,
        videoUrl: node.data.n.videoUrl,
        imageUrl: node.data.n.imageUrl,
        flowchartId: this.flowchartId,
        videos: this.videos,
        data: {
          type: node.data.n.type,
          title: this.$t('message.add_title'),
          description: this.$t('message.add_description'),
        }
      });
    },
    onDragStart() {
      //onDragStart(event) {
      //console.log('onDragStart', event);
      this.dragging = true;
    },
    archiveFlowchart() {
      Api().delete("flowcharts/admins/managedacademy/" +
        this.$store.state.managedAcademy +
        "/flowchart/" + this.flowchartId)
        .then(() => {
          this.navigateTo({ name: 'flowcharts' });
        })
        .catch(err => {
          if (err)
            this.showAlert('error', this.$t('message.oops_not_working'));
        });
    },
    buildAvailableBlocks() {
      let blocks = [];
      for (const t of this.techniqueTypes) {
        blocks.push(
          {
            type: t,
            title: this.getTechniqueTitle(t),
            description: this.$t('message.add_description'),
            data: {
              type: t,
              title: this.$t('message.add_title'),
              description: this.$t('message.add_description'),
            },
            preview: {
              title: this.$t('message.submission'),
            },
            node: {
              flowchartId: this.flowchartId,
              type: t,
              title: this.$t('message.add_title'),
              description: this.$t('message.add_description'),
              data: {
                type: t,
                title: this.$t('message.add_title'),
                description: this.$t('message.add_description'),
              }
            }
          }
        );
      }
      this.blocks = blocks;
    },
  }
};
</script>

<style scoped>
.flowy-drag-handle button {
  cursor: grab;
}

.flowy-block.draggable-mirror {
  opacity: 0.6;
}

.flowy-wrapper {
  width: 100%;
  overflow-x: auto;
}

.flowchart-container {
  padding: 5px;
  border-radius: 5px;
  background-color: #8866ff10;
  margin-bottom: 8rem !important;
  display: inline-block;
  /* or block, depending on Flowy layout */
  min-width: max-content;
  /* ensures it grows to fit children */
  white-space: nowrap;
  /* helpful if nodes are inline */
  position: relative;
}

flowy-new-block {
  width: 100px;
}


.top-functions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}
</style>